import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { bankTransactionStagingActions as slice } from "../slices/bankTransactionStaging";
import { getErrorMessage } from "../../utils/errorUtils";
import { BankTransactionStagingResponseDto } from "../../shared/dtos/bankTransaction/BankTransactionStagingResponseDto";
import { PostStagedTransactionRequestDto } from "../../shared/dtos/bankTransaction/PostStagedTransactionRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getStagedTransactions =
  (page: number = 1, size: number = 10) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingStagedTransactions());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<BankTransactionStagingResponseDto>>
      >("/bank-transactions-staging", {
        headers: { requiresAuth: true },
        params: { page, size },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(slice.setStagedTransactions(data.data.content));
        dispatch(slice.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(slice.setErrorLoadingStagedTransactions(getErrorMessage(error)));
    }
  };

export const uploadBankStatement =
  (file: File) => async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingStagedTransactionUpload());
    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/bank-transactions-staging/upload",
        formData,
        {
          headers: {
            requiresAuth: true,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "File uploaded and matched!");
        dispatch(slice.uploadStagedTransactionSuccess());
        dispatch(getStagedTransactions() as any);
        dispatch(
          slice.setServerResponseStatus({
            operation: "upload",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorUploadingStagedTransaction(message));
    }
  };

export const postStagedTransaction =
  (values: PostStagedTransactionRequestDto) =>
  async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingStagedTransactionPost());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/bank-transactions-staging/post",
        values,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(
          data.message || "Transaction posted and allocated successfully!",
        );
        dispatch(slice.postStagedTransactionSuccess(values.stagingId));
        dispatch(
          slice.setServerResponseStatus({
            operation: "post",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorPostingStagedTransaction(message));
    }
  };

export const deleteStagedTransaction =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(slice.setLoadingStagedTransactionDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/bank-transactions-staging/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Transaction discarded.");
        dispatch(slice.deleteStagedTransactionSuccess(id));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(slice.setErrorDeletingStagedTransaction(message));
    }
  };

export const resetStagingStatusAction = () => (dispatch: AppDispatch) => {
  dispatch(slice.setServerResponseStatus(null));
};
